import { getCompanyFunds } from 'lib/api/funds';
import { getCessionStatusBatch, retryCession } from 'lib/api/reSell/factoring';
import { errorHandler } from 'lib/functions/errors';
import { showAlert, validatePermissions } from 'lib/functions/utils';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CessionType, ResellDocument } from '../../types';

interface CessionStatusResponse {
  folio: string;
  docType: string;
  businessIdentifier: string;
  isAccepted?: boolean;
  isProcessing?: boolean;
  isRejected?: boolean;
  isQuerySuccessful?: boolean;
  UrlAec?: string;
  aecUrl?: string;
  [key: string]: unknown;
}

interface CessionStatusManagerProps {
  resells: ResellDocument[];
  setResells: React.Dispatch<React.SetStateAction<ResellDocument[]>>;
  readOnly: boolean;
  nomineeId?: string;
  hasCessionIntegration: boolean;
  fundId: string;
  bankAccountId: string;
}

const CessionStatusManager: React.FC<CessionStatusManagerProps> = ({
  resells,
  setResells,
  readOnly,
  nomineeId,
  hasCessionIntegration,
  fundId,
  bankAccountId,
}) => {
  const dispatch = useDispatch();
  const { data: funds } = getCompanyFunds(dispatch);
  const user = useSelector(
    (state: { auth: { user?: { companyId: number; roles?: any[] } } }) =>
      state.auth.user,
  );

  useEffect(() => {
    const fetchCessionStatus = async () => {
      if (!hasCessionIntegration) {
        return;
      }

      if (!readOnly || !nomineeId || resells.length === 0) {
        return;
      }

      try {
        setResells((prevResells) =>
          prevResells.map((resell) => ({
            ...resell,
            cessionStatus: { isLoading: true },
          })),
        );

        const batchRequests = resells.map((resell) => ({
          folio: resell.folio,
          docType: resell.docType || resell.subType || '',
          businessIdentifier: resell.businessIdentifier,
          stakeholderIdentifier: resell.stakeholderIdentifier,
        }));

        const cessionStatuses = await getCessionStatusBatch(
          dispatch,
          batchRequests,
        );

        // Función para crear el handler de reintento
        const createOnRecede = (resellData: ResellDocument) => () => {
          void (async () => {
            if (!user?.companyId || !fundId || !bankAccountId) {
              showAlert(
                dispatch,
                'error',
                `Faltan datos necesarios para reintentar la cesión`,
              );
              return;
            }

            try {
              setResells((prev) =>
                prev.map((r) =>
                  r.id === resellData.id
                    ? {
                        ...r,
                        cessionStatus: r.cessionStatus
                          ? { ...r.cessionStatus, isReceding: true }
                          : { isReceding: true },
                      }
                    : r,
                ),
              );

              const fund = funds?.find((f) => f.id === fundId);
              if (!fund) {
                throw new Error('Fondo no encontrado');
              }

              const retryPayload = {
                companyId: Number(user.companyId),
                type: CessionType.FUND,
                documents: [
                  {
                    id: resellData.documentId,
                    resellId: resellData.id,
                    folio: resellData.folio,
                    docType: resellData.docType || resellData.subType || '',
                    subType: resellData.docType || resellData.subType || '',
                    businessIdentifier: resellData.businessIdentifier,
                    stakeholderIdentifier: resellData.stakeholderIdentifier,
                    amount: resellData.amount,
                    originalAmount: resellData.originalAmount,
                    advance: resellData.advance,
                    arrearRate: resellData.arrearRate,
                    bankAccountId: parseInt(bankAccountId, 10),
                    cessionDate: resellData.cessionDate
                      ? new Date(resellData.cessionDate)
                      : new Date(),
                    startDate: new Date(resellData.startDate),
                    endDate: new Date(resellData.endDate),
                    rate: String(resellData.rate || 0),
                    rateCost: String(resellData.rateCost || 0),
                    operationCostFixed: String(
                      resellData.operationCostFixed || 0,
                    ),
                    operationCostVariable: String(
                      resellData.operationCostVariable || 0,
                    ),
                    fund: {
                      id: fund.id,
                      name: fund.name,
                    },
                  },
                ],
              };

              await retryCession(dispatch, retryPayload);

              setResells((prev) =>
                prev.map((r) =>
                  r.id === resellData.id
                    ? {
                        ...r,
                        cessionStatus: {
                          isProcessing: true,
                          isReceding: false,
                          isLoading: false,
                          onRecede: r.cessionStatus?.onRecede,
                        },
                      }
                    : r,
                ),
              );

              showAlert(
                dispatch,
                'success',
                'Re-cesión iniciada correctamente',
              );
            } catch (error) {
              errorHandler(dispatch, error, 'Error al reintentar cesión');

              setResells((prev) =>
                prev.map((r) =>
                  r.id === resellData.id
                    ? {
                        ...r,
                        cessionStatus: r.cessionStatus
                          ? { ...r.cessionStatus, isReceding: false }
                          : undefined,
                      }
                    : r,
                ),
              );
            }
          })();
        };

        const statusesArray: CessionStatusResponse[] =
          (cessionStatuses as { results?: CessionStatusResponse[] })?.results ||
          [];
        setResells((prevResells) =>
          prevResells.map((resell) => {
            const cessionStatus = statusesArray.find(
              (status: CessionStatusResponse) =>
                String(status.folio).trim() === String(resell.folio).trim() &&
                status.businessIdentifier === resell.businessIdentifier,
            );

            if (cessionStatus) {
              return {
                ...resell,
                cessionStatus: {
                  isAccepted: cessionStatus.isAccepted,
                  isProcessing: cessionStatus.isProcessing,
                  isRejected: cessionStatus.isRejected,
                  isReceding: false,
                  isQuerySuccessful: cessionStatus.isQuerySuccessful,
                  isLoading: false,
                  canRetry: validatePermissions(
                    user,
                    'financing_cession_retry',
                  ),
                  UrlAec: cessionStatus.UrlAec || cessionStatus.aecUrl,
                  onRecede: createOnRecede(resell),
                },
              };
            }

            return {
              ...resell,
              cessionStatus: { isLoading: false },
            };
          }),
        );
      } catch (error) {
        console.error('Error al obtener estado de cesión:', error);
        setResells((prevResells) =>
          prevResells.map((resell) => ({
            ...resell,
            cessionStatus: { isLoading: false },
          })),
        );
      }
    };

    void fetchCessionStatus();
  }, [
    hasCessionIntegration,
    readOnly,
    nomineeId,
    resells.length,
    dispatch,
    user?.companyId,
    fundId,
    bankAccountId,
    funds,
    setResells,
  ]);
  return null;
};

export default CessionStatusManager;
